/**
 * Auto decision-mode theo NGƯỠNG TIỀN (US-E17.F1-0188-S01 · @UC9251 @UC9252 @UC9253) — @gate money,config.
 *
 * THUẦN: không DB, không I/O, deterministic. Trả về mode HIỆU LỰC cho 1 khoản chi dựa trên SỐ TIỀN:
 * chi nhỏ (gửi xe 20k) tự duyệt, chi lớn (50tr) bắt buộc vote — P3.1 financial-principles.
 *
 * LUẬT (2 ngưỡng, `[` = inclusive):
 *   amount <  autoApproveMinor                         ⇒ `admin_only`  (treasurer/owner tự duyệt)
 *   [autoApproveMinor, voteRequiredMinor)              ⇒ `silent`      (im lặng-duyệt khoản vừa — F3 xử finalize)
 *   amount >= voteRequiredMinor                        ⇒ VOTE bắt buộc: `ratio` nếu quỹ đã cấu hình
 *                                                        `expense_decision_mode=ratio` (tôn trọng tỉ lệ
 *                                                        nhóm đã chọn), ngược lại `majority`.
 *   autoModeEnabled = false                            ⇒ `staticMode`  (hành vi CŨ — KHÔNG regress)
 *
 * FAIL-CLOSED (cửa ngõ tiền): config bẩn (ngưỡng âm / autoApprove > voteRequired / staticMode lạ) hoặc
 * amount âm ⇒ trả `majority` (mode CỨNG NHẤT — bắt vote, KHÔNG tự chi). KHÔNG throw để không kẹt luồng
 * (caller log). Tiền = BigInt minor, 0 float, 0 Number() cast.
 */

/** Mode hiệu lực sau resolve. `silent` = phiên im-lặng-duyệt (mechanism F3 `0190`). */
export const RESOLVED_DECISION_MODES = [
  "admin_only",
  "silent",
  "majority",
  "ratio",
] as const;
export type ResolvedDecisionMode = (typeof RESOLVED_DECISION_MODES)[number];

/** Mode TĨNH đã cấu hình (fallback khi auto-mode tắt) — khớp `expense_decision_mode` hiện hành. */
export const STATIC_DECISION_MODES = ["admin_only", "majority", "ratio"] as const;
export type StaticDecisionMode = (typeof STATIC_DECISION_MODES)[number];

/** Mode CỨNG NHẤT — fail-closed rơi về đây (bắt vote, KHÔNG tự duyệt). */
export const FAIL_CLOSED_DECISION_MODE: ResolvedDecisionMode = "majority";

/** 3 khoá registry `system_settings` (override được ở Group.settings / Fund.settings). */
export const EXPENSE_AUTO_MODE_ENABLED_KEY = "expense_auto_mode_enabled";
export const EXPENSE_AUTO_APPROVE_MINOR_KEY = "expense_auto_approve_threshold_minor";
export const EXPENSE_VOTE_REQUIRED_MINOR_KEY = "expense_vote_required_threshold_minor";

export const DECISION_MODE_KEYS = [
  EXPENSE_AUTO_MODE_ENABLED_KEY,
  EXPENSE_AUTO_APPROVE_MINOR_KEY,
  EXPENSE_VOTE_REQUIRED_MINOR_KEY,
] as const;
export type DecisionModeKey = (typeof DECISION_MODE_KEYS)[number];

/**
 * DEFAULT hệ thống — auto-mode **TẮT** ⇒ quỹ cũ chạy y nguyên `expense_decision_mode` tĩnh (0 regress).
 * Ngưỡng chỉ có hiệu lực khi nhóm/quỹ CHỦ ĐỘNG bật `expense_auto_mode_enabled`.
 * Tiền lưu dưới dạng CHUỖI BigInt trong jsonb (no-float).
 */
export const DECISION_MODE_DEFAULTS: {
  readonly expense_auto_mode_enabled: boolean;
  readonly expense_auto_approve_threshold_minor: string;
  readonly expense_vote_required_threshold_minor: string;
} = {
  expense_auto_mode_enabled: false,
  expense_auto_approve_threshold_minor: "100000",
  expense_vote_required_threshold_minor: "1000000",
};

/** Config đã resolve (fund > group > system) đưa vào hàm thuần. */
export interface DecisionModeConfig {
  readonly autoModeEnabled: boolean;
  readonly autoApproveMinor: bigint;
  readonly voteRequiredMinor: bigint;
  readonly staticMode: StaticDecisionMode;
}

/** True nếu `key` là khoá auto-decision-mode (type-guard cho registry). */
export function isDecisionModeKey(key: string): key is DecisionModeKey {
  return (DECISION_MODE_KEYS as readonly string[]).includes(key);
}

/** True nếu `value` là mode tĩnh hợp lệ (type-guard resolver fail-closed). */
export function isStaticDecisionMode(value: unknown): value is StaticDecisionMode {
  return (
    typeof value === "string" &&
    (STATIC_DECISION_MODES as readonly string[]).includes(value)
  );
}

/**
 * True nếu `value` là CHUỖI BigInt minor ≥ 0 (không float, không số JS — tiền qua wire là string).
 * Dùng bởi resolver (đọc jsonb) + contract refine.
 */
export function isDecisionThresholdMinor(value: unknown): value is string {
  if (typeof value !== "string" || value.trim() === "") return false;
  if (!/^\d+$/.test(value.trim())) return false;
  try {
    return BigInt(value.trim()) >= 0n;
  } catch {
    return false;
  }
}

/** Parse chuỗi BigInt minor → bigint; giá trị bẩn ⇒ `null` (caller fail-closed). */
export function parseDecisionThresholdMinor(value: unknown): bigint | null {
  return isDecisionThresholdMinor(value) ? BigInt(value.trim()) : null;
}

/**
 * Mode HIỆU LỰC cho khoản chi `amountMinor` (BigInt minor). Thuần & deterministic.
 * Fail-closed → `majority` khi config/amount bẩn (KHÔNG throw).
 */
export function resolveDecisionMode(
  amountMinor: bigint,
  cfg: DecisionModeConfig,
): ResolvedDecisionMode {
  // Auto-mode TẮT ⇒ đường CŨ (mode tĩnh). staticMode bẩn ⇒ fail-closed majority.
  if (cfg.autoModeEnabled !== true) {
    return isStaticDecisionMode(cfg.staticMode) ? cfg.staticMode : FAIL_CLOSED_DECISION_MODE;
  }

  // Config ngưỡng bẩn (âm / đảo ngược) hoặc amount bẩn (âm) ⇒ fail-closed BẮT VOTE.
  if (
    typeof cfg.autoApproveMinor !== "bigint" ||
    typeof cfg.voteRequiredMinor !== "bigint" ||
    typeof amountMinor !== "bigint" ||
    cfg.autoApproveMinor < 0n ||
    cfg.voteRequiredMinor < 0n ||
    amountMinor < 0n ||
    cfg.autoApproveMinor > cfg.voteRequiredMinor
  ) {
    return FAIL_CLOSED_DECISION_MODE;
  }

  if (amountMinor < cfg.autoApproveMinor) return "admin_only";
  if (amountMinor < cfg.voteRequiredMinor) return "silent";
  // ≥ ngưỡng bắt-vote: tôn trọng `ratio` nếu quỹ đã chọn ratio (tỉ lệ thông qua + deadline đã cấu hình);
  // mọi trường hợp khác (admin_only / bẩn) ⇒ `majority` (fail-closed — KHÔNG cho tự duyệt).
  return cfg.staticMode === "ratio" ? "ratio" : "majority";
}
